// frontend/src/utils/zodiacCompatibility.js

// --- Normalización: quita acentos y baja a minúsculas ---
export function normalizeSign(sign = '') {
  return String(sign)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // sin tildes
}

// --- Mapa de nombre canónico (con acentos correctos) ---
const CANONICAL_BY_NORMALIZED = {
  aries: 'Aries',
  tauro: 'Tauro',
  geminis: 'Géminis',
  cancer: 'Cáncer',
  leo: 'Leo',
  virgo: 'Virgo',
  libra: 'Libra',
  escorpio: 'Escorpio',
  sagitario: 'Sagitario',
  capricornio: 'Capricornio',
  acuario: 'Acuario',
  piscis: 'Piscis',
  taurus: 'Tauro',
  gemini: 'Géminis',
  scorpio: 'Escorpio',
  sagittarius: 'Sagitario',
  capricorn: 'Capricornio',
  aquarius: 'Acuario',
  pisces: 'Piscis',
};

export function toCanonical(sign = '') {
  const norm = normalizeSign(sign);
  return CANONICAL_BY_NORMALIZED[norm] || null;
}

// --- Signo por fecha (devuelve canónico o 'Desconocido') ---
export function getZodiacSign(dateString) {
  const [year, month, day] = String(dateString || '').split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return 'Desconocido';
  }

  const d = day;
  const m = month;

  // El orden importa en los días cúspide (por ejemplo 20 de enero → Acuario).
  if ((m === 1 && d >= 20) || (m === 2 && d <= 18)) return 'Acuario';
  if ((m === 2 && d >= 19) || (m === 3 && d <= 20)) return 'Piscis';
  if ((m === 3 && d >= 21) || (m === 4 && d <= 19)) return 'Aries';
  if ((m === 4 && d >= 20) || (m === 5 && d <= 20)) return 'Tauro';
  if ((m === 5 && d >= 21) || (m === 6 && d <= 20)) return 'Géminis';
  if ((m === 6 && d >= 21) || (m === 7 && d <= 22)) return 'Cáncer';
  if ((m === 7 && d >= 23) || (m === 8 && d <= 22)) return 'Leo';
  if ((m === 8 && d >= 23) || (m === 9 && d <= 22)) return 'Virgo';
  if ((m === 9 && d >= 23) || (m === 10 && d <= 22)) return 'Libra';
  if ((m === 10 && d >= 23) || (m === 11 && d <= 21)) return 'Escorpio';
  if ((m === 11 && d >= 22) || (m === 12 && d <= 21)) return 'Sagitario';
  if ((m === 12 && d >= 22) || (m === 1 && d <= 19)) return 'Capricornio';

  return 'Desconocido';
}

// --- Opuestos complementarios (180°) ---
const OPPOSITES = {
  Aries: 'Libra',
  Tauro: 'Escorpio',
  'Géminis': 'Sagitario',
  'Cáncer': 'Capricornio',
  Leo: 'Acuario',
  Virgo: 'Piscis',

  Libra: 'Aries',
  Escorpio: 'Tauro',
  Sagitario: 'Géminis',
  Capricornio: 'Cáncer',
  Acuario: 'Leo',
  Piscis: 'Virgo',
};

export function getOppositeSign(sign) {
  const c = toCanonical(sign);
  return c ? OPPOSITES[c] || null : null;
}

export function isOppositePair(a, b) {
  const A = toCanonical(a);
  const B = toCanonical(b);
  if (!A || !B) return false;
  return OPPOSITES[A] === B; // simétrico por definición
}

// --- Compatibilidades (mismo elemento + cruces favorables clásicos) ---
// Fuego: Aries, Leo, Sagitario
// Tierra: Tauro, Virgo, Capricornio
// Aire : Géminis, Libra, Acuario
// Agua : Cáncer, Escorpio, Piscis
const COMPATIBLE_MAP = {
  Aries:       ['Leo', 'Sagitario', 'Géminis', 'Acuario'],
  Tauro:       ['Virgo', 'Capricornio', 'Cáncer', 'Piscis'],
  'Géminis':   ['Libra', 'Acuario', 'Aries', 'Leo'],
  'Cáncer':    ['Escorpio', 'Piscis', 'Tauro', 'Virgo'],
  Leo:         ['Aries', 'Sagitario', 'Géminis', 'Libra'],
  Virgo:       ['Tauro', 'Capricornio', 'Cáncer', 'Escorpio'],
  Libra:       ['Géminis', 'Acuario', 'Leo', 'Sagitario'],
  Escorpio:    ['Cáncer', 'Piscis', 'Virgo', 'Capricornio'],
  Sagitario:   ['Aries', 'Leo', 'Libra', 'Acuario'],
  Capricornio: ['Tauro', 'Virgo', 'Escorpio', 'Piscis'],
  Acuario:     ['Géminis', 'Libra', 'Aries', 'Sagitario'],
  Piscis:      ['Cáncer', 'Escorpio', 'Tauro', 'Capricornio'],
};

export function getCompatibleSigns(sign) {
  const c = toCanonical(sign);
  return c ? (COMPATIBLE_MAP[c] || []) : [];
}

export function isCompatiblePair(a, b) {
  const list = getCompatibleSigns(a);
  const B = toCanonical(b);
  return !!(B && list.includes(B));
}

// --- (Opcional) Emojis por signo, útil para UI ---
export const SIGN_EMOJI = {
  Aries: '♈', Tauro: '♉', 'Géminis': '♊', 'Cáncer': '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Escorpio: '♏', Sagitario: '♐', Capricornio: '♑', Acuario: '♒', Piscis: '♓',
};

// --- Export conveniente para consumir en front y back ---
const ZodiacCompatibility = {
  normalizeSign,
  toCanonical,
  getZodiacSign,
  getOppositeSign,
  isOppositePair,
  getCompatibleSigns,
  isCompatiblePair,
  SIGN_EMOJI,
};
export default ZodiacCompatibility;